<template>
    <div class="food-menu">
        <BlocksStationsFunctionsFoodMenu
            :menu="computedMenu"
            :selected="selection"
            @select="select"
        />
        <div>
            <BlocksCLCards
                v-if="computeCards.length > 0"
                :key="redraw"
                class="inline product-cards"
                :count-override="display.count"
                :title="categoryTitle"
                :description="computeDescription"
                :cards="computeCards"
                :card-background="display.transparentBackground === 'true' ? 'none' : 'white'"
                :food-menu="display.component === 'foodMenu'"
            >
                <template #external-link>
                    <nuxt-link
                        v-if="preorderLink"
                        class="preorder-link"
                        :to="preorderLink"
                    >
                        {{ $t('preorderLink') }}
                    </nuxt-link>
                </template>
            </BlocksCLCards>
        </div>
    </div>
</template>
<script>
import { mapState } from 'pinia';
import { usePageStore } from '~dsc/stores/cms/page';

export default {
    name: 'StationsFunctionsFood',
    props: {
        display: {
            type: Object,
            required: false,
            default: () => ({
                component: 'foodMenu',
                count: 'many',
            }),
        },
        cards: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data: () => ({
        selection: {
            root: 0,
            category: 0,
        },
        redraw: 0,
    }),
    computed: {
        ...mapState(usePageStore, ['station']),
        menuItems() {
            if (this.station && this.station?.menu?.items) {
                return this.station.menu.items;
            } else if (this.cards.length > 0 && this.cards[0]) {
                return this.cards[0].items;
            }

            return [];
        },
        computedMenu() {
            if (this.menuItems.filter(this.isProductMenu).length > 0) {
                return this.menuItems.map((item, key) => ({
                    id: item.id,
                    index: key,
                    title: item.title,
                    description: item[key]?.description ?? this.station.menu?.description,
                    items: item.items?.filter(this.isProductMenu).map(({ id, title, description }, index) => ({
                        id,
                        index,
                        title,
                        description,
                    })),
                })) ?? [];
            }

            return [];
        },
        preorderLink() {
            if (this.station?.stationTypes?.includes('helmisimpukkarestaurant')) {
                return `${this.$paths.getPath('preorder')}?station=${this.station.slug}`;
            }

            return false;
        },
        categoryTitle() {
            if (this.menuItems.filter(this.isProduct).length === this.menuItems.length) {
                return this.cards[0].description ?? null;
            } else if (this.menuItems[this.selection.root].items.length > 0) {
                return this.menuItems[this.selection.root].items[this.selection.category].title;
            } else if (this.menuItems[this.selection.root].title) {
                return this.menuItems[this.selection.root].title;
            }

            return '';
        },
        computeDescription() {
            if (this.menuItems.filter(this.isProduct).length === this.menuItems.length) {
                return this.menuItems.description;
            }

            const menuDescription = this.cards[0]?.description ? this.cards[0].description : this.station?.menu?.description;
            let description = menuDescription || this.$t('listPriceNotification');

            if (this.menuItems[this.selection.root].description) ({ description } = this.menuItems[this.selection.root]);

            if (this.menuItems[this.selection.root].items[this.selection.category] && this.menuItems[this.selection.root].items[this.selection.category].items) {
                return this.menuItems[this.selection.root].items[this.selection.category].description
                    ? this.menuItems[this.selection.root].items[this.selection.category].description
                    : description;
            }

            return description;
        },
        computeCards() {
            let currentCards = [];
            let categoryCards = [];
            if (this.menuItems.filter(this.isProduct).length === this.menuItems.length) {
                currentCards = this.menuItems;
            } else {
                categoryCards = this.menuItems[this.selection.root].items.filter(this.isProduct);
                if (this.menuItems[this.selection.root].items[this.selection.category] && this.menuItems[this.selection.root].items[this.selection.category].items) {
                    currentCards = this.menuItems[this.selection.root].items[this.selection.category].items;
                } else if (categoryCards) {
                    currentCards = categoryCards;
                }
            }

            if (!currentCards.length) {
                return [];
            }

            return currentCards.map(item => ({
                id: item.id,
                internal: item.internal,
                byline: item.specialties ? { specialties: item.specialties } : '',
                title: item.name,
                image: item.image,
                text: item.description,
                tags: item.tags,
                price: item.variations ? item.variations.map(priceVar => ({
                    title: priceVar.name,
                    price: priceVar.price,
                })) : [{ title: this.$t('price'), price: item.price }],
            }));
        },
    },
    mounted() {
        this.parseRootMenuFromQuery();
    },
    methods: {
        select(payload) {
            this.$set(this, 'selection', payload);
            this.updateRootMenuToQuery(payload);
            this.redraw = this.$dsc.uuidv4();
        },
        parseRootMenuFromQuery() {
            const { menuId, categoryId } = this.parseUrlQuery();
            if (menuId) {
                const { index: root, items } = this.computedMenu.find(({ id }) => id === menuId);
                if (root >= 0) {
                    let category = 0;
                    if (categoryId && items?.length > 0) {
                        const matchingCategory = items.find(({ id }) => id === categoryId);
                        if (matchingCategory) {
                            category = matchingCategory.index;
                        }
                    }

                    this.select({
                        root,
                        category,
                    });
                }
            }
        },
        updateRootMenuToQuery(payload) {
            const { menuId, categoryId } = payload;
            if (menuId) {
                const newQueryParams = { menuId };
                if (categoryId) {
                    newQueryParams.categoryId = categoryId;
                }

                this.updateUrlQuery(newQueryParams);
            }
        },
        isProductMenu({ contentType }) {
            return [
                'productMenu',
                'helmisimpukkaProductMenu',
            ].includes(contentType);
        },
        isProduct({ contentType }) {
            return [
                'product',
                'helmisimpukkaProduct',
            ].includes(contentType);
        },
    },
};
</script>
<style scoped>
.food-menu .preorder-link {
  transition: all 0.2s ease-in-out;
  color: var(--color-primary-700);
  font-weight: 700;
}
.food-menu .preorder-link:hover {
  color: var(--color-primary-900);
}

.product-cards {
  padding-top: 1rem;
}
</style>
